/**
 * The one carrier between "the script generator KNOWS this statement destroys
 * stored data" and "the execute path asks the user before running it".
 *
 * ### Why a marker rather than a pattern
 *
 * The always-dangerous pattern table matches statement TEXT. An `ALTER COLUMN` statement carries
 * only the type the column is moving TO:
 *
 *   ALTER TABLE [dbo].[Facturas] ALTER COLUMN [Importe] DECIMAL(10, 2) NOT NULL;
 *
 * That text is byte-identical whether the column was `DECIMAL(10, 4)` (every
 * stored value is silently rounded, unrecoverably — MEASURED, see
 * `columnNarrowingHazard.ts`) or `DECIMAL(10, 1)` (a widening, lossless). The
 * before-state is simply not in the statement, so **no** text pattern can tell
 * the destructive case from the harmless one, and a blanket
 * `ALTER .* COLUMN` rule would confirm on every column change in every script —
 * which trains people to dismiss the dialog, the failure mode this gate exists
 * to avoid.
 *
 * The distinction exists in exactly one place: the schema comparer's
 * before/after column snapshots, inside `planAlterColumn`. So the verdict is
 * computed THERE, from the two snapshots, and travels to the execute path as a
 * tag on the `-- [REVIEW]` line the generator already emits above the
 * statement. The gate then keys on what the operation would DO, not on what it
 * looks like.
 *
 * ### Why the script text is the channel
 *
 * The generated script leaves the app: it is opened in a query tab, executed
 * later, edited, saved, copied into a ticket, run by a colleague. A structured
 * payload handed to the tab at open time survives none of that, and the very
 * first surface it would miss is "Open in Query Tab" + Execute five minutes
 * later. A comment line travels with the statement everywhere the statement
 * goes, and is deleted along with it.
 *
 * ### Contract
 *
 * - Only `formatDataLossReviewLine` produces the tag. Nothing else writes the
 *   literal string, so producer and matcher cannot drift.
 * - The tag means **stored data is destroyed and cannot be recovered by
 *   reversing the change**. It does NOT mean "this might fail", "this changes
 *   query semantics", or "Jam could not script this" — those keep the plain
 *   `-- [REVIEW]` prefix and deliberately raise no dialog.
 * - `hasDataLossReviewLine` is matched against the RAW statement text, before
 *   any string-literal stripping. Prose in a comment must never be able to
 *   decide whether a destructive statement is confirmed.
 */

/** The tag itself. Deliberately readable — it is shown to the user in the script. */
export const DATA_LOSS_REVIEW_TAG = '[DATA LOSS]';

/**
 * A `--` comment line carrying the tag. Anchored to the start of a line and to
 * the `--` so that only a comment can carry it: prose inside a string literal
 * or an identifier cannot.
 */
const DATA_LOSS_REVIEW_LINE = /^[ \t]*--[ \t]*\[REVIEW\][ \t]*\[DATA LOSS\][ \t]/im;

/**
 * Emit the review line for a hazard that destroys stored data.
 *
 * Apostrophes in `text` are no longer load-bearing. The gate used to
 * strip string literals without skipping comments, so an odd number of them
 * anywhere in a comment flipped the whole script to the conservative "Could not
 * parse query structure" verdict (MEASURED) — a false alarm on a clean script,
 * which is exactly what this gate must not manufacture. INJ-S5-1 removed the
 * mechanism: comments are now stripped before that scan, and
 * `dangerousQueryComments.test.ts` pins it. Writing prose without apostrophes
 * is still the house style here, but it is no longer a correctness constraint.
 *
 * What IS load-bearing, more so than before: this line is a `--` comment, and
 * everything downstream of `hasDataLossReviewLine` now reads a comment-free
 * script. The tag is detectable at exactly one point in the risk gate, and
 * that point must stay ahead of the strip.
 */
export const formatDataLossReviewLine = (text: string): string =>
  `-- [REVIEW] ${DATA_LOSS_REVIEW_TAG} ${text}`;

/** Does this SQL carry a generator-issued data-loss disclosure? */
export const hasDataLossReviewLine = (sql: string): boolean => DATA_LOSS_REVIEW_LINE.test(sql);

/**
 * The statement a data-loss disclosure is about: the first executable line
 * below the tagged comment.
 *
 * Line-based on purpose. The generator emits the disclosure directly above the
 * statement it describes, and reading it that way needs no statement splitting
 * — which matters because the splitter gives up on a query whose quotes do not
 * balance, and a disclosure sentence is prose that may well contain an
 * apostrophe. Empty string when the tag is absent or nothing executable
 * follows it.
 */
export const dataLossStatementPreview = (sql: string): string => {
  const lines = sql.split('\n');
  const taggedAt = lines.findIndex((line) => DATA_LOSS_REVIEW_LINE.test(line));
  if (taggedAt === -1) return '';
  const statement = lines
    .slice(taggedAt + 1)
    .map((line) => line.trim())
    .find((line) => line.length > 0 && !line.startsWith('--'));
  return (statement ?? '').substring(0, 100);
};
